import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";

// HESAP & AYARLAR — the console hook layer over the two READY per-staff PERSONAL
// preference routes (BE-4 · secondary-surfaces §3, backend #237):
//   • GET /v1/me/preferences  → the CALLER's own settings (defaults materialised
//                               server-side on first read, so the client always
//                               gets a concrete object).
//   • PUT /v1/me/preferences  → a partial merge onto the caller's own row.
// Both are SELF-scoped (no id parameter). This is PERSONAL settings, NOT business
// config. Follows the console read/write pattern (useQuery/useMutation + useApi).
// The shapes mirror the API contract (preferences.service `StaffPreferencesResponse`
// + validation `updateStaffPreferencesSchema`) — the enums are the schema's bounds.

export type StaffLocale = "tr" | "en";
export type StaffDefaultScope = "own" | "team";
export type NotificationSeverityThreshold = "all" | "critical_only";
export type AccessibilityFontScale = "normal" | "large";

/** The feed severity floor (bounded jsonb). "all" = every operational signal;
 *  "critical_only" = suppress normal-severity items in the Bildirimler feed. */
export interface NotificationPref {
  severityThreshold: NotificationSeverityThreshold;
}

/** Accessibility preferences (bounded jsonb) — all optional-with-defaults server-side. */
export interface AccessibilityPref {
  reducedMotion: boolean;
  highContrast: boolean;
  fontScale: AccessibilityFontScale;
}

/** GET response — the caller's OWN settings only. */
export interface StaffPreferences {
  locale: StaffLocale;
  defaultScope: StaffDefaultScope;
  notificationPref: NotificationPref;
  accessibility: AccessibilityPref;
  updatedAt: string;
}

/** PUT body — a partial merge; each present field replaces the stored value
 *  (nested jsonb objects wholesale). At least one field must be present. */
export interface UpdateStaffPreferences {
  locale?: StaffLocale;
  defaultScope?: StaffDefaultScope;
  notificationPref?: NotificationPref;
  accessibility?: AccessibilityPref;
}

const PREFS_KEY = ["me", "preferences"];

/** GET /v1/me/preferences — the caller's own preferences. No retry: the server
 *  materialises defaults on first read, so a miss is a real error, not a transient. */
export function usePreferences() {
  const api = useApi();
  return useQuery({
    queryKey: PREFS_KEY,
    retry: false,
    queryFn: () => api.get<StaffPreferences>("/v1/me/preferences"),
  });
}

/** PUT /v1/me/preferences — persist a partial change. Writes the authoritative
 *  server response straight back into the cache (the source of truth) on success. */
export function useUpdatePreferences() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateStaffPreferences) =>
      api.request<StaffPreferences>("/v1/me/preferences", { method: "PUT", body }),
    onSuccess: (data) => {
      qc.setQueryData(PREFS_KEY, data);
    },
  });
}

// ── Foto / imza (personal media) — #244, the object-storage follow-on ──────────
// Self-scoped over /v1/me/media (backend #244). GET lists the caller's own media
// metadata; POST :kind uploads (multipart, encrypted at rest, supersedes); DELETE
// removes. The bytes themselves stream via api.getBlob (owner-decrypt), rendered
// as an object URL by the surface. :kind is a KIND, not an id — never another's.

/** The route param for a media kind (maps to the API's PHOTO/SIGNATURE). */
export type StaffMediaKind = "photo" | "signature";

/** Metadata for one stored media object (no bytes — those stream via getBlob). */
export interface StaffMediaMeta {
  kind: "PHOTO" | "SIGNATURE";
  contentType: string;
  sizeBytes: number;
  updatedAt: string;
}

const MEDIA_KEY = ["me", "media"];

/** GET /v1/me/media — the caller's own uploaded media metadata (honest-empty). */
export function useStaffMedia() {
  const api = useApi();
  return useQuery({
    queryKey: MEDIA_KEY,
    retry: false,
    queryFn: async () => {
      const res = await api.get<{ media?: StaffMediaMeta[] }>("/v1/me/media");
      return res?.media ?? [];
    },
  });
}

/** POST /v1/me/media/:kind — upload the caller's own photo/signature (multipart). */
export function useUploadMedia() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file }: { kind: StaffMediaKind; file: File }) => {
      const form = new FormData();
      form.append("file", file);
      return api.postForm<StaffMediaMeta>(`/v1/me/media/${kind}`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_KEY }),
  });
}

/** DELETE /v1/me/media/:kind — remove the caller's own photo/signature. */
export function useDeleteMedia() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: StaffMediaKind) => api.delete<void>(`/v1/me/media/${kind}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEDIA_KEY }),
  });
}
